import React, { useEffect, useContext } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@mui/material";
import { ModalSpinner } from "./ModalSpinner";
import { API } from "aws-amplify";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { GlobalContext } from "store/globalContext";

const AddCardForm = ({ isPaymentMethodExists, reloadAfterAddCard }) => {
  const { userData, loadUserData } = useContext(GlobalContext);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const [clientSecret, setClientSecret] = React.useState("");
  const [error, setError] = React.useState(null);
  const [isSettingUp, setIsSettingUp] = React.useState(false);

  const cardElementOptions = {
    // hidePostalCode: true,
    disableLink: true,
    paymentMethodOrder: ["card"],
    style: {
      base: {
        fontSize: "14px",
        color: "#000",
        "::placeholder": {
          color: "#ccc",
        },
      },
      invalid: {
        color: "#e74c3c",
      },
    },
    layout: "tabs",
    disabled: !clientSecret,
  };

  const handleSubmit = async (event) => {
    setError(null);
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSettingUp(true);
    const cardElement = elements.getElement(CardElement);

    try {
      const { error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {},
        },
      });

      if (error) {
        setError(error.message);
        return;
      }
      reloadAfterAddCard(false);
      loadUserData(userData?.cognitoId);
    } catch (err) {
      setError(err.message);
      return;
    } finally {
      setIsSettingUp(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await API.post("restApiV2", "/payment/set-up/by-user", {
          body: {},
        });

        if (result?.error) {
          console.error(result?.message || result.error);
          dispatch(showError(result?.message || result.error));
          return true;
        }

        if (result?.clientSecret) {
          setClientSecret(result.clientSecret);
          return;
        }
        setError("Failed to fetch client secret");
      } catch (err) {
        setError("Failed to fetch client secret");
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2 p-6 max-w-96 border border-solid border-gray-400 rounded-3xl">
        <CardElement
          onChange={() => setError(null)}
          options={cardElementOptions}
        />
        <div className="min-h-6 text-sm p-0 m-0">
          {error && <div className="text-red-700">{error}</div>}
        </div>
        <Button
          variant="contained"
          type="submit"
          disabled={!stripe || !clientSecret || isSettingUp}
        >
          {isPaymentMethodExists ? "Update Card" : "Add Card"}
        </Button>
      </div>
      {((!clientSecret && !error) || isSettingUp) && <ModalSpinner open />}
    </form>
  );
};

export default AddCardForm;
